import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import GetAppIcon from '@mui/icons-material/GetApp';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

export const INITIATIVE_TABS = [
  'Chat',
  'Adoption',
  'Collaboration',
  'Impact',
  'Trend Analysis',
  'Performance Email',
];

const MONTH_OPTIONS = [
  { label: 'Cumulative Period', value: 'Cumulative' },
  { label: 'Apr-26', value: 'Apr-26' },
  { label: 'May-26', value: 'May-26' },
  { label: 'Jun-26', value: 'Jun-26' },
];

const STATE_OPTIONS = ['East', 'West', 'North', 'South'];
const OWNER_OPTIONS = [];
const REPORT_OPTIONS = [];

const emptyMetric = (unit = 'MT', unit2 = '%') => ({
  value: '',
  unit,
  unit2,
});

const buildInitialFilters = (data = {}, mode = 'edit') => {
  const now = new Date();
  const createdOn =
    data.created_on ||
    now.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return {
    kpi: data.project_objective || data.kpi || '',
    project_id: data.project_id || '',
    project_description: data.project_description || '',
    account_owner: data.account_owner || '',
    account_owner_title: data.account_owner_title || '',
    report_to: data.report_to || '',
    report_to_title: data.report_to_title || '',
    created_on: createdOn,
    net_improvement: data.net_improvement || emptyMetric('%', 'Unit'),
    expected_savings: data.expected_savings || emptyMetric('Rs', 'MT'),
    previous_actual: data.previous_actual || emptyMetric('MT', '%'),
    current_plan: data.current_plan || emptyMetric('MT', '%'),
    current_target: data.current_target || emptyMetric('MT', '%'),
    enabling_one_description:
      data.enabling_one_description ||
      (mode === 'edit'
        ? 'Enable partner activation programs in target markets to support volume uplift.'
        : ''),
    enabling_two_description:
      data.enabling_two_description ||
      (mode === 'edit'
        ? 'Align premium mix incentives with account owners for focused districts.'
        : ''),
  };
};

const NumberWithUnit = ({ label, value, unit, unit2, onValueChange, onUnitChange, onUnit2Change, disabled }) => (
  <Box className="number-unit">
    <TextField
      label={label}
      size="small"
      fullWidth
      type="number"
      value={value === undefined || value === null ? '' : value}
      onChange={(e) => onValueChange(e.target.value)}
      disabled={disabled}
    />
    <TextField
      className="unit-field"
      label="Unit"
      size="small"
      value={unit || ''}
      onChange={(e) => onUnitChange(e.target.value)}
      disabled={disabled}
    />
    <TextField
      className="unit-field"
      label="Unit"
      size="small"
      value={unit2 || ''}
      onChange={(e) => onUnit2Change(e.target.value)}
      disabled={disabled}
    />
  </Box>
);

const EmptyPanel = ({ label }) => (
  <div className="empty-state">{label || 'No data available'}</div>
);

const FilterSelect = ({ label, value, onChange, options, minWidth = 180 }) => (
  <TextField
    select
    size="small"
    sx={{ minWidth }}
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    <MenuItem value="">{label}</MenuItem>
    {options.map((s) => (
      <MenuItem key={s} value={s}>
        {s}
      </MenuItem>
    ))}
  </TextField>
);

const ADOPTION_HEADERS = [
  'State',
  'Account Owner',
  'Report To',
  'Total No. of Logins',
  'Last Login',
  'Last Session Time (min)',
  'Total Session Time (min)',
];

const AdoptionPanel = () => {
  const [month, setMonth] = useState(MONTH_OPTIONS[0].value);
  const [state, setState] = useState('');
  const [owner, setOwner] = useState('');
  const [reportTo, setReportTo] = useState('');

  return (
    <Box>
      <Box mb={1.5} maxWidth={300}>
        <TextField
          select
          size="small"
          fullWidth
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {MONTH_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box className="filter-row">
        <FilterSelect label="Select State" value={state} onChange={setState} options={STATE_OPTIONS} />
        <FilterSelect
          label="Select Account owner"
          value={owner}
          onChange={setOwner}
          options={OWNER_OPTIONS}
          minWidth={200}
        />
        <FilterSelect
          label="Select Report to"
          value={reportTo}
          onChange={setReportTo}
          options={REPORT_OPTIONS}
          minWidth={200}
        />
        <Button className="btn-apply" variant="contained">
          Apply
        </Button>
        <IconButton
          className="btn-reset"
          size="small"
          onClick={() => {
            setState('');
            setOwner('');
            setReportTo('');
            setMonth(MONTH_OPTIONS[0].value);
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <Box className="filter-actions">
          <Tooltip title="Download Excel">
            <IconButton size="small">
              <GetAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Fullscreen">
            <IconButton size="small">
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <div className="hdr-table-wrap">
        <table className="hdr-table">
          <thead>
            <tr>
              {ADOPTION_HEADERS.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
        </table>
      </div>
    </Box>
  );
};

const ChatPanel = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      from: 'System',
      body: 'Please review the activation plan for the focus zone.',
      at: '24 Aug 2026 10:12',
      mine: false,
    },
    {
      id: 2,
      from: 'You',
      body: 'Noted. Will share the updated target mix by EOD.',
      at: '24 Aug 2026 11:05',
      mine: true,
    },
  ]);
  const [draft, setDraft] = useState('');

  const send = () => {
    if (!draft.trim()) return;
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        from: 'You',
        body: draft.trim(),
        at: new Date().toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        mine: true,
      },
    ]);
    setDraft('');
  };

  return (
    <Box>
      <div className="chat-list">
        {messages.map((m) => (
          <div key={m.id} className={`chat-bubble${m.mine ? ' mine' : ''}`}>
            <div className="meta">
              {m.from} · {m.at}
            </div>
            <div className="body">{m.body}</div>
          </div>
        ))}
      </div>
      <Box className="chat-compose">
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          placeholder="Type a message..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button
          variant="contained"
          onClick={send}
          sx={{ backgroundColor: '#215093', textTransform: 'none', height: 40 }}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

const KANBAN_COLUMNS = [
  'Activity Plan',
  'Account Owner Update',
  'Action Requested',
  'Action Taken',
  'Issue Recomended',
  'Issue Closed',
];

const COLLAB_FILTERS = [
  { key: 'zone', label: 'Select Zone', options: ['East', 'West', 'North', 'South'] },
  { key: 'region', label: 'Select Region', options: ['Region A', 'Region B', 'Region C'] },
  { key: 'issue', label: 'Select Issue category', options: ['Supply', 'Demand', 'Service'] },
  { key: 'segment', label: 'Select Dealer segment', options: ['A', 'B', 'C'] },
  { key: 'owner', label: 'Select Account owner', options: OWNER_OPTIONS },
  { key: 'report', label: 'Select Report to', options: REPORT_OPTIONS },
];

const CollaborationPanel = () => {
  const [filters, setFilters] = useState({});

  return (
    <Box>
      <Box className="filter-row">
        {COLLAB_FILTERS.map((f) => (
          <FilterSelect
            key={f.key}
            label={f.label}
            value={filters[f.key] || ''}
            onChange={(v) => setFilters((prev) => ({ ...prev, [f.key]: v }))}
            options={f.options}
            minWidth={170}
          />
        ))}
        <Button className="btn-apply" variant="contained">
          Apply
        </Button>
        <IconButton className="btn-reset" size="small" onClick={() => setFilters({})}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <div className="collab-board">
        <div className="collab-total">Total Issues: 0</div>
        <div className="collab-columns">
          {KANBAN_COLUMNS.map((col) => (
            <div className="collab-col" key={col}>
              <div className="collab-chevron">{col}</div>
              <div className="collab-count">No. of Issues (0)</div>
              <div className="collab-body" />
            </div>
          ))}
        </div>
      </div>
    </Box>
  );
};

const ImpactPanel = () => (
  <Box>
    <Box className="filter-row" justifyContent="flex-end" mb={1}>
      <Box className="filter-actions" ml={0}>
        <Tooltip title="Download Excel">
          <IconButton size="small">
            <GetAppIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fullscreen">
          <IconButton size="small">
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
    <div className="hdr-table-wrap">
      <table className="hdr-table">
        <thead>
          <tr>
            <th rowSpan={2}>Region</th>
            <th colSpan={2}>FY26</th>
            <th colSpan={2}>FY27</th>
            <th colSpan={5}>Apr-26</th>
            <th rowSpan={2}>Collaboration</th>
          </tr>
          <tr className="sub">
            <th>Actual (MT)</th>
            <th>Trade %</th>
            <th>Target (MT)</th>
            <th>Target (%)</th>
            <th>Total Volume (MT)</th>
            <th>Trade %</th>
            <th>Target (MT)</th>
            <th>Target (%)</th>
            <th>Gap To Target (+/- MT)</th>
          </tr>
        </thead>
      </table>
    </div>
  </Box>
);

const TrendAnalysisPanel = () => (
  <Box>
    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: '#215093' }}>
      Trend Analysis
    </Typography>
    <EmptyPanel label="No trend analysis data available" />
  </Box>
);

const PE_HEADERS = [
  '',
  'Action Owner To List',
  'Action Owner CC List',
  'State',
  'Region',
  'District',
  'Dealers',
  'Clap Target',
  'MTD Target',
  'MTD Actual',
  'MTD Achievement',
];

const PE_KPIS = [
  { label: 'Unique AOs', value: '0', sub: null, color: '#2752bc', Icon: PersonOutlineIcon },
  { label: 'Emails Ready', value: '0', sub: '- Valid Email', color: '#2752bc', Icon: MailOutlineIcon },
  { label: 'Missing Emails', value: '0', sub: '- No Email ID', color: '#d32f2f', Icon: WarningAmberIcon },
  { label: 'Attachments Generated', value: '0', sub: '- One Excel / AO', color: '#2e7d32', Icon: AttachFileIcon },
  { label: 'Total Dealers Coverage', value: '0', sub: '- Across All AOs', color: '#6a1b9a', Icon: PeopleAltIcon },
  { label: 'Reminder', value: '0', sub: null, color: '#f87022', Icon: NotificationsActiveIcon },
];

const PerformanceEmailPanel = () => {
  const [filters, setFilters] = useState({
    owner: '',
    status: '',
    state: '',
    region: '',
    achievement: '',
  });

  return (
    <Box>
      <Box className="pe-actions">
        <Button
          variant="contained"
          size="small"
          startIcon={<MailOutlineIcon />}
          sx={{ textTransform: 'none', backgroundColor: '#215093' }}
        >
          View Email
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ textTransform: 'none', backgroundColor: '#215093' }}
        >
          Trigger Emails to 0 AOs
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<NotificationsActiveIcon />}
          sx={{ textTransform: 'none', backgroundColor: '#215093' }}
        >
          Trigger Reminder to 0 AOs
        </Button>
        <Box flexGrow={1} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<GetAppIcon />}
          sx={{ textTransform: 'none', borderColor: '#215093', color: '#215093' }}
        >
          Download All Attachments
        </Button>
      </Box>
      <div className="pe-hint">
        Send performance emails to Account Owners with dealer-wise Excel attachments
      </div>

      <div className="pe-kpis">
        {PE_KPIS.map((kpi) => (
          <div className="pe-kpi" key={kpi.label}>
            <div className="icon" style={{ background: `${kpi.color}22`, color: kpi.color }}>
              <kpi.Icon fontSize="small" />
            </div>
            <div>
              <div className="label">{kpi.label}</div>
              <div className="value" style={{ color: kpi.color }}>
                {kpi.value}
                {kpi.sub ? <span className="sub"> {kpi.sub}</span> : null}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Box className="filter-row">
        <FilterSelect
          label="Account Owner"
          value={filters.owner}
          onChange={(v) => setFilters((p) => ({ ...p, owner: v }))}
          options={OWNER_OPTIONS}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(v) => setFilters((p) => ({ ...p, status: v }))}
          options={['Ready', 'Missing Email', 'Sent']}
        />
        <FilterSelect
          label="State"
          value={filters.state}
          onChange={(v) => setFilters((p) => ({ ...p, state: v }))}
          options={STATE_OPTIONS}
        />
        <FilterSelect
          label="Region"
          value={filters.region}
          onChange={(v) => setFilters((p) => ({ ...p, region: v }))}
          options={['Region A', 'Region B']}
        />
        <FilterSelect
          label="Achievement %"
          value={filters.achievement}
          onChange={(v) => setFilters((p) => ({ ...p, achievement: v }))}
          options={['<50%', '50-80%', '>80%']}
        />
        <Button className="btn-apply" variant="contained">
          Apply
        </Button>
        <IconButton className="btn-reset" size="small" onClick={() => setFilters({
          owner: '', status: '', state: '', region: '', achievement: '',
        })}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <div className="hdr-table-wrap">
        <table className="hdr-table">
          <thead>
            <tr>
              {PE_HEADERS.map((h, idx) => (
                <th key={h || `chk-${idx}`}>{h || <input type="checkbox" disabled />}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={PE_HEADERS.length}>No data available</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Box>
  );
};


export const InitiativeTabsSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  return (
    <>
      <Box className="tabs-wrap">
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { backgroundColor: '#343c8c' } }}
        >
          {INITIATIVE_TABS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>
      <Box className="tab-panel">
        {activeTab === 0 && <ChatPanel />}
        {activeTab === 1 && <AdoptionPanel />}
        {activeTab === 2 && <CollaborationPanel />}
        {activeTab === 3 && <ImpactPanel />}
        {activeTab === 4 && <TrendAnalysisPanel />}
        {activeTab === 5 && <PerformanceEmailPanel />}
      </Box>
    </>
  );
};
